import { getStorage } from 'firebase/storage';
import React from 'react';
import Attachment from '../../../constants/attachment';
import { useFirebase } from '../../../services/firebase';
import {
  getCachedDownloadUrl,
  getCachedBlob,
  getCachedUrlSync,
} from '../../../services/firebase/storage';
import { LOADING_STATE } from '../../../util/loading';
import ImagePlaceholder from '../../common/ImagePlaceholder';
import { CaseImageProps } from './CaseImage';
import CaseVideo, { CaseVideoProps } from './CaseVideo';
import heic2any from 'heic2any';

export interface CaseAttachmentProps extends React.HTMLAttributes<HTMLElement> {
  fileId: string;
  userId: string;
  imageProps?: Partial<CaseImageProps>;
  videoProps?: Partial<CaseVideoProps>;
}

function getAttachmentTypeFromContentType(contentType?: string | null) {
  if (contentType?.endsWith('heic')) {
    return Attachment.HEIC;
  }
  if (contentType?.startsWith('image')) {
    return Attachment.IMAGE;
  }
  if (contentType?.startsWith('video')) {
    return Attachment.VIDEO;
  }

  return Attachment.UNKNOWN;
}

interface FileState {
  fileUrl?: string;
  contentType?: string | null;
  attachmentType?: Attachment;
  loadingStatus: LOADING_STATE;
}

interface HEICCache {
  [fileUrl: string]: string;
}

function useFile(fileId: string, userId: string) {
  const isBlob = fileId.startsWith('blob:');
  const { app } = useFirebase();

  if (!(window as any).heicCache) {
    (window as any).heicCache = {} as HEICCache;
  }

  // Check cache synchronously for instant display
  const cachedData = !isBlob ? getCachedUrlSync(fileId, userId) : null;

  const [state, setState] = React.useState<FileState>({
    fileUrl: isBlob ? fileId : cachedData?.url,
    contentType: cachedData?.contentType || null,
    attachmentType: cachedData
      ? getAttachmentTypeFromContentType(cachedData.contentType)
      : undefined,
    loadingStatus:
      isBlob || cachedData ? LOADING_STATE.LOADED : LOADING_STATE.LOADING,
  });

  React.useEffect(
    (async () => {
      if (!app || isBlob) {
        return;
      }

      // Skip fetch if we already have cached data from initial state
      if (cachedData) {
        return;
      }

      // Set loading state for uncached files
      setState((prev) => ({ ...prev, loadingStatus: LOADING_STATE.LOADING }));

      const storage = getStorage(app);

      // Use cached download URL with metadata
      const { url: fileUrl, contentType } = await getCachedDownloadUrl(
        storage,
        fileId,
        userId
      );
      const attachmentType = getAttachmentTypeFromContentType(contentType);

      // If the uploaded files are HEIC (iOS), we need to convert them to PNG
      if (attachmentType === Attachment.HEIC) {
        try {
          let convertedUrl: string;
          if ((window as any).heicCache[fileUrl]) {
            console.log('Using cached HEIC conversion', fileUrl);
            convertedUrl = (window as any).heicCache[fileUrl];
          } else {
            // Use cached blob fetch
            const { blob: heicBlob } = await getCachedBlob(
              storage,
              fileId,
              userId
            );
            const convertedBlob = (await heic2any({
              blob: heicBlob,
              toType: 'image/png',
              quality: 0.5,
            })) as Blob;
            convertedUrl = URL.createObjectURL(convertedBlob);
            console.log('Converted HEIC to image', convertedUrl);

            // Cache the converted URL so we don't have to convert it again this session
            (window as any).heicCache[fileUrl] = convertedUrl;
          }

          setState((prevState) => ({
            ...prevState,
            fileUrl: convertedUrl,
            contentType: 'image/png',
            attachmentType: Attachment.IMAGE,
            loadingStatus: LOADING_STATE.LOADED,
          }));
          return;
        } catch (e) {
          console.error('Error converting HEIC to image', e);
        }
      }
      setState((prevState) => ({
        ...prevState,
        contentType,
        fileUrl,
        attachmentType,
        loadingStatus: LOADING_STATE.LOADED,
      }));
    }) as any,
    [fileId, userId, app, isBlob, cachedData]
  );

  return state;
}

const CaseAttachment: React.FC<CaseAttachmentProps> = ({
  fileId,
  userId,
  imageProps,
  videoProps,
  ...props
}) => {
  const { fileUrl, attachmentType, loadingStatus, contentType } = useFile(
    fileId,
    userId
  );
  if (loadingStatus === LOADING_STATE.LOADING) {
    return <ImagePlaceholder {...imageProps?.placeholderProps} {...props} />;
  }
  if (fileId.startsWith('blob:')) {
    return <img src={fileUrl} {...props} />;
  }

  if (attachmentType === Attachment.IMAGE) {
    const { placeholderProps, ...otherImageProps } = imageProps || {};
    return <img src={fileUrl} {...otherImageProps} {...props} />;
  }
  if (attachmentType === Attachment.VIDEO) {
    return (
      <CaseVideo
        file={fileUrl!}
        contentType={contentType}
        {...videoProps}
        {...props}
      />
    );
  }

  return null;
};

export default CaseAttachment;
